import axios from 'axios';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Navigate, Link } from 'react-router-dom';
import { Loader2, Store, AlertCircle, ArrowRight } from 'lucide-react';
import { vendorApi } from '@/services/api';
import { useVendorStore, type Vendor } from '@/store/vendor-store';
import { Button, Input } from '@nepal-ecom/ui';

// Vendor registration lives on the storefront app (multi-step KYC flow), not in
// the dashboard, so "Become a vendor" links out to it rather than a /register
// route that doesn't exist here.
const STOREFRONT_URL =
  (import.meta.env.VITE_STOREFRONT_URL as string | undefined) ?? 'http://localhost:3000';

const schema = z.object({
  email: z.string().email('Enter a valid email'),
  password: z.string().min(6, 'At least 6 characters'),
});

type FormData = z.infer<typeof schema>;

const PERKS = [
  'Reach customers in all 7 provinces',
  'COD and digital payments built-in',
  'Weekly automated payouts',
];

export function Login() {
  const { setTokens, setVendor, isAuthenticated } = useVendorStore();
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    setError,
  } = useForm<FormData>({ resolver: zodResolver(schema) });

  if (isAuthenticated) return <Navigate to="/" replace />;

  const onSubmit = async (data: FormData) => {
    try {
      const res = await vendorApi.login(data.email, data.password);
      const { accessToken, refreshToken, user } = res.data.data;
      if (user.role !== 'vendor') {
        setError('root', { message: 'This account is not a vendor account.' });
        return;
      }
      setTokens(accessToken, refreshToken);
      // Load the real vendor profile so the dashboard knows the true KYC /
      // approval status (pending vs approved) instead of always showing
      // "pending". Falls back to sensible defaults if the profile isn't ready.
      let profile: {
        id?: string;
        businessName?: string;
        tier?: Vendor['tier'];
        verificationStatus?: Vendor['kycStatus'];
      } = {};
      try {
        const me = await vendorApi.getMyVendor();
        profile = me.data?.data ?? me.data ?? {};
      } catch {
        // No vendor profile yet (e.g. account created but KYC not submitted).
      }
      setVendor({
        id: profile.id || user.id,
        name: user.fullName || user.email,
        email: user.email,
        storeName: profile.businessName || '',
        tier: profile.tier || 'basic',
        kycStatus: profile.verificationStatus || 'pending',
        avatar: user.avatarUrl,
      });
    } catch (err) {
      const status = axios.isAxiosError(err) ? err.response?.status : undefined;
      if (status === 401) setError('root', { message: 'Invalid email or password.' });
      else setError('root', { message: 'Could not reach the server. Try again in a moment.' });
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 grid lg:grid-cols-2">
      {/* Form column */}
      <div className="flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-sm">
          <div className="mb-8">
            <Link to="/" className="inline-flex items-center gap-2 mb-8">
              <span className="h-9 w-9 rounded-xl bg-gradient-to-br from-primary-500 to-primary-700 text-white font-bold flex items-center justify-center shadow-md shadow-primary-900/20">श</span>
              <span className="font-display text-xl font-bold text-slate-900">
                Shishya<span className="text-primary-600">stra</span>
              </span>
            </Link>
            <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Welcome back</h1>
            <p className="text-sm text-slate-500 mt-1">Sign in to manage your store.</p>
          </div>

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <div className="space-y-1.5">
              <label className="block text-xs font-semibold text-slate-700">Email</label>
              <Input
                {...register('email')}
                type="email"
                placeholder="vendor@example.com"
              />
              {errors.email && (
                <p className="text-xs text-red-500 mt-1">{errors.email.message}</p>
              )}
            </div>

            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <label className="block text-xs font-semibold text-slate-700">Password</label>
                <a
                  href={`${STOREFRONT_URL}/forgot-password`}
                  className="text-xs font-semibold text-primary-600 hover:underline"
                >
                  Forgot?
                </a>
              </div>
              <Input {...register('password')} type="password" placeholder="••••••••" />
              {errors.password && (
                <p className="text-xs text-red-500 mt-1">{errors.password.message}</p>
              )}
            </div>

            {errors.root && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700 flex items-start gap-2">
                <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                {errors.root.message}
              </div>
            )}

            <Button type="submit" disabled={isSubmitting} size="lg" className="w-full">
              {isSubmitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Sign in <ArrowRight className="h-4 w-4" /></>}
            </Button>
          </form>

          <p className="text-sm text-slate-600 mt-6 text-center">
            New to Shishyastra?{' '}
            <a
              href={`${STOREFRONT_URL}/vendor/register`}
              className="font-semibold text-primary-600 hover:underline"
            >
              Become a vendor
            </a>
          </p>
        </div>
      </div>

      {/* Brand column */}
      <div className="hidden lg:block sidebar-surface relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute -top-32 -right-32 w-96 h-96 bg-primary-500/30 rounded-full blur-3xl" />
          <div className="absolute -bottom-32 -left-20 w-80 h-80 bg-cobalt-500/20 rounded-full blur-3xl" />
        </div>
        <div className="relative h-full flex flex-col justify-center px-12 text-white">
          <div className="max-w-md">
            <Store className="h-10 w-10 text-secondary-300 mb-5" />
            <h2 className="text-3xl font-bold leading-tight tracking-tight">
              Sell to the whole of Nepal
            </h2>
            <p className="text-white/75 mt-3 leading-relaxed">
              Join 12,000+ Nepali makers, brands, and retailers growing online with Shishyastra.
            </p>
            <ul className="mt-8 space-y-3">
              {PERKS.map((p) => (
                <li key={p} className="flex items-start gap-3 text-sm text-white/90">
                  <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-secondary-400 flex-shrink-0" />
                  {p}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
